import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { teamApi } from "@/lib/api";
import { usePasswordPolicyHint } from "@/hooks/usePasswordPolicyHint";
import { useAppStore } from "@/store/useAppStore";

export default function AcceptInvitePage() {
  const passwordHint = usePasswordPolicyHint();
  const navigate = useNavigate();
  const { search } = useLocation();
  const refreshAuth = useAppStore((s) => s.refreshAuth);
  const token = new URLSearchParams(search).get("token") || "";
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!token) {
    return (
      <AuthLayout eyebrow="Invite" headline="Invitation missing">
        <p className="text-[13px] text-slate-600 mb-3">This link doesn&apos;t include an invitation token.</p>
        <Button className="w-full sm:w-auto h-11 sm:h-10" onClick={() => navigate("/login")}>Back to login</Button>
      </AuthLayout>
    );
  }

  const submit = (e) => {
    e.preventDefault();
    if (!displayName) return toast.error("Enter your name");
    if (password !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    teamApi.acceptInvite(token, password, displayName)
      .then(async () => {
        toast.success("Welcome to the team!");
        if (refreshAuth) await refreshAuth();
        navigate("/today");
      })
      .catch((err) => {
        const code = err?.code;
        if (code === "invite_unusable") toast.error("This invitation is no longer valid");
        else if (code === "invite_not_found") toast.error("Invitation not found");
        else if (code === "email_already_registered") toast.error("This email is already registered. Please log in.");
        else if (code === "weak_password") toast.error(err?.message || "Password does not meet requirements");
        else toast.error(err?.message || "Could not accept invite");
        setBusy(false);
      });
  };

  return (
    <AuthLayout eyebrow="Invite" headline="Accept your invitation" sub="Set a password and join the workspace.">
      <form onSubmit={submit} className="space-y-4 min-w-0 w-full" data-testid="accept-invite-form">
        <div className="min-w-0">
          <label htmlFor="accept-invite-name" className="text-[11.5px] uppercase tracking-wider text-slate-500 font-semibold">Your name</label>
          <Input
            id="accept-invite-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter your name"
            className="h-11 sm:h-9 mt-1 w-full min-w-0"
            data-testid="accept-invite-name"
            autoFocus
            autoComplete="name"
          />
        </div>
        <div className="min-w-0">
          <label htmlFor="accept-invite-password" className="text-[11.5px] uppercase tracking-wider text-slate-500 font-semibold">Password</label>
          <Input
            id="accept-invite-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            className="h-11 sm:h-9 mt-1 w-full min-w-0"
            data-testid="accept-invite-password"
            autoComplete="new-password"
          />
          {passwordHint ? (
            <p className="text-[11px] text-slate-500 mt-1 break-words">{passwordHint}</p>
          ) : null}
        </div>
        <div className="min-w-0">
          <label htmlFor="accept-invite-confirm" className="text-[11.5px] uppercase tracking-wider text-slate-500 font-semibold">Confirm password</label>
          <Input
            id="accept-invite-confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm your password"
            className="h-11 sm:h-9 mt-1 w-full min-w-0"
            data-testid="accept-invite-confirm"
            autoComplete="new-password"
          />
        </div>
        <Button type="submit" className="w-full h-11 sm:h-10" disabled={busy} data-testid="accept-invite-submit">
          {busy ? "Joining…" : "Accept invite & continue"}
        </Button>
      </form>
    </AuthLayout>
  );
}
